import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clientApi } from '../services/client';
import extractApiError from '../utils/extractApiError';
import type {
  PatrolServicePlan,
  RequiredVisit,
  VisitType,
  TimeWindowType,
  VisitCriticality,
} from '../types';

// ── Static option tables ────────────────────────────────────────────────

const VISIT_TYPE_OPTIONS: { value: VisitType; label: string }[] = [
  { value: 'routine_patrol', label: 'Routine Patrol' },
  { value: 'drive_through', label: 'Drive-Through' },
  { value: 'foot_patrol', label: 'Foot Patrol' },
  { value: 'lockup', label: 'Lockup' },
  { value: 'unlock', label: 'Unlock' },
  { value: 'perimeter_check', label: 'Perimeter Check' },
  { value: 'amenity_check', label: 'Amenity Check' },
  { value: 'parking_enforcement', label: 'Parking Enforcement' },
  { value: 'special_inspection', label: 'Special Inspection' },
];

const TIME_WINDOW_OPTIONS: { value: TimeWindowType; label: string; hint: string }[] = [
  { value: 'fixed', label: 'Fixed', hint: 'Exactly at one time (with a small tolerance).' },
  { value: 'window', label: 'Window', hint: 'Any time within a narrow window (e.g. 9:45–10:15 PM).' },
  { value: 'broad', label: 'Broad', hint: 'Any time within a broad window (e.g. 8–11 PM first patrol).' },
  { value: 'random', label: 'Random', hint: 'Any time between start and end, no specific timing.' },
];

const CRITICALITY_OPTIONS: { value: VisitCriticality; label: string; hint: string }[] = [
  { value: 'critical', label: 'Critical', hint: 'Missing this fires a high-severity rule.' },
  { value: 'required', label: 'Required', hint: 'Standard obligation. Medium severity if missed.' },
  { value: 'routine', label: 'Routine', hint: 'Nice-to-have. Light penalty if missed.' },
  { value: 'optional', label: 'Optional', hint: 'Informational only, no scoring impact.' },
];

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Common task and evidence tags — free-add is allowed via the input.
const TASK_SUGGESTIONS = [
  'perimeter', 'doors', 'gate_check', 'lights', 'pool', 'trash_area',
  'parking', 'stairwells', 'basement', 'lobby',
];
const EVIDENCE_SUGGESTIONS = [
  'dar_timestamp', 'photo', 'checkpoint_scan', 'video',
];

// ── Local, editable visit shape (id becomes optional for un-saved rows) ─

type EditableVisit = Partial<RequiredVisit> & {
  // Client-only key to keep React happy while a new row has no server id
  _key: string;
};

const nextKey = (() => {
  let n = 0;
  return () => `new-${++n}`;
})();

function emptyVisit(order: number, defaults?: { effective_from?: string }): EditableVisit {
  return {
    _key: nextKey(),
    visit_type: 'routine_patrol',
    time_window_type: 'window',
    time_window_start: '21:00:00',
    time_window_end: '22:00:00',
    days_of_week: [0, 1, 2, 3, 4, 5, 6],
    frequency_per_day: 1,
    expected_duration_min_minutes: null,
    expected_duration_max_minutes: null,
    required_tasks: [],
    required_evidence: [],
    criticality: 'required',
    effective_from: defaults?.effective_from ?? new Date().toISOString().slice(0, 10),
    effective_to: null,
    display_order: order,
    label: '',
  };
}

// ── Chip picker ─────────────────────────────────────────────────────────

function ChipPicker({
  label, value, suggestions, onChange,
}: {
  label: string;
  value: string[];
  suggestions: string[];
  onChange: (next: string[]) => void;
}) {
  const [input, setInput] = useState('');
  const add = (tag: string) => {
    const t = tag.trim();
    if (!t || value.includes(t)) return;
    onChange([...value, t]);
  };
  const remove = (tag: string) => onChange(value.filter((t) => t !== tag));
  const availableSuggestions = suggestions.filter((s) => !value.includes(s));

  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <div className="flex flex-wrap gap-1 mb-2">
        {value.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 rounded-full bg-brand-50 text-brand-700 border border-brand-200 px-2 py-0.5 text-xs"
          >
            {t}
            <button
              type="button"
              onClick={() => remove(t)}
              className="text-brand-500 hover:text-brand-800"
              aria-label={`Remove ${t}`}
            >
              &times;
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-1 mb-1">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add(input);
              setInput('');
            }
          }}
          placeholder="Type a tag and press Enter"
          className="flex-1 rounded-md border border-slate-300 px-2 py-1 text-xs"
        />
        <button
          type="button"
          onClick={() => { add(input); setInput(''); }}
          className="px-2 py-1 text-xs rounded-md border border-slate-300 hover:bg-slate-50"
        >
          Add
        </button>
      </div>
      {availableSuggestions.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {availableSuggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => add(s)}
              className="text-xs text-slate-500 hover:text-brand-600 underline"
            >
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── One editable visit row ──────────────────────────────────────────────

function VisitRow({
  visit, onChange, onDelete, onMoveUp, onMoveDown, canMoveUp, canMoveDown,
}: {
  visit: EditableVisit;
  onChange: (patch: Partial<EditableVisit>) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}) {
  const toggleDay = (d: number) => {
    const days = visit.days_of_week ?? [];
    const next = days.includes(d) ? days.filter((x) => x !== d) : [...days, d].sort();
    onChange({ days_of_week: next });
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
      {/* Row 1: label + reorder + delete */}
      <div className="flex items-center gap-2">
        <input
          type="text"
          placeholder="Label (e.g. Nightly Lockup)"
          value={visit.label ?? ''}
          onChange={(e) => onChange({ label: e.target.value })}
          className="flex-1 rounded-md border border-slate-300 px-2 py-1 text-sm font-medium"
        />
        <button
          type="button"
          disabled={!canMoveUp}
          onClick={onMoveUp}
          className="px-2 py-1 text-xs rounded-md border border-slate-300 hover:bg-slate-50 disabled:opacity-40"
          aria-label="Move up"
        >
          &uarr;
        </button>
        <button
          type="button"
          disabled={!canMoveDown}
          onClick={onMoveDown}
          className="px-2 py-1 text-xs rounded-md border border-slate-300 hover:bg-slate-50 disabled:opacity-40"
          aria-label="Move down"
        >
          &darr;
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="px-2 py-1 text-xs rounded-md border border-red-300 text-red-600 hover:bg-red-50"
        >
          Remove
        </button>
      </div>

      {/* Row 2: type + criticality */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Visit Type</label>
          <select
            value={visit.visit_type ?? 'routine_patrol'}
            onChange={(e) => onChange({ visit_type: e.target.value as VisitType })}
            className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm"
          >
            {VISIT_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Criticality</label>
          <select
            value={visit.criticality ?? 'required'}
            onChange={(e) => onChange({ criticality: e.target.value as VisitCriticality })}
            className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm"
          >
            {CRITICALITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <p className="mt-0.5 text-[11px] text-slate-500">
            {CRITICALITY_OPTIONS.find((o) => o.value === visit.criticality)?.hint}
          </p>
        </div>
      </div>

      {/* Row 3: time window controls */}
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Time Window</label>
        <div className="grid grid-cols-4 gap-2 mb-2">
          {TIME_WINDOW_OPTIONS.map((o) => (
            <label
              key={o.value}
              className={`flex items-center gap-1 rounded-md border px-2 py-1 text-xs cursor-pointer ${
                visit.time_window_type === o.value
                  ? 'border-brand-500 bg-brand-50 text-brand-800'
                  : 'border-slate-300 hover:border-slate-400'
              }`}
            >
              <input
                type="radio"
                name={`tw-${visit._key}`}
                checked={visit.time_window_type === o.value}
                onChange={() => {
                  const patch: Partial<EditableVisit> = { time_window_type: o.value };
                  // When switching TO fixed, clear the end.
                  if (o.value === 'fixed') patch.time_window_end = null;
                  onChange(patch);
                }}
                className="text-brand-500"
              />
              {o.label}
            </label>
          ))}
        </div>
        <p className="mb-2 text-[11px] text-slate-500">
          {TIME_WINDOW_OPTIONS.find((o) => o.value === visit.time_window_type)?.hint}
        </p>
        <div className="flex gap-2 items-end">
          <div>
            <label className="block text-[11px] text-slate-500">Start</label>
            <input
              type="time"
              value={(visit.time_window_start ?? '').slice(0, 5)}
              onChange={(e) => onChange({ time_window_start: e.target.value ? `${e.target.value}:00` : null })}
              className="rounded-md border border-slate-300 px-2 py-1 text-sm"
            />
          </div>
          {visit.time_window_type !== 'fixed' && (
            <div>
              <label className="block text-[11px] text-slate-500">End</label>
              <input
                type="time"
                value={(visit.time_window_end ?? '').slice(0, 5)}
                onChange={(e) => onChange({ time_window_end: e.target.value ? `${e.target.value}:00` : null })}
                className="rounded-md border border-slate-300 px-2 py-1 text-sm"
              />
            </div>
          )}
          <div className="ml-auto">
            <label className="block text-[11px] text-slate-500">Times per day</label>
            <input
              type="number"
              min={1}
              value={visit.frequency_per_day ?? 1}
              onChange={(e) => onChange({ frequency_per_day: Math.max(1, Number(e.target.value) || 1) })}
              className="w-16 rounded-md border border-slate-300 px-2 py-1 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Row 4: days of week */}
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Days of Week</label>
        <div className="flex gap-1">
          {DAY_LABELS.map((label, i) => {
            const on = (visit.days_of_week ?? []).includes(i);
            return (
              <button
                key={i}
                type="button"
                onClick={() => toggleDay(i)}
                className={`w-9 px-1 py-1 text-xs rounded-md border ${
                  on
                    ? 'border-brand-500 bg-brand-50 text-brand-800'
                    : 'border-slate-300 text-slate-500 hover:border-slate-400'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Row 5: duration */}
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">
          Expected Duration <span className="text-slate-400">(min / max minutes, optional)</span>
        </label>
        <div className="flex gap-2 items-center">
          <input
            type="number"
            min={0}
            value={visit.expected_duration_min_minutes ?? ''}
            placeholder="min"
            onChange={(e) => onChange({
              expected_duration_min_minutes: e.target.value === '' ? null : Number(e.target.value),
            })}
            className="w-20 rounded-md border border-slate-300 px-2 py-1 text-sm"
          />
          <span className="text-xs text-slate-400">to</span>
          <input
            type="number"
            min={0}
            value={visit.expected_duration_max_minutes ?? ''}
            placeholder="max"
            onChange={(e) => onChange({
              expected_duration_max_minutes: e.target.value === '' ? null : Number(e.target.value),
            })}
            className="w-20 rounded-md border border-slate-300 px-2 py-1 text-sm"
          />
        </div>
      </div>

      {/* Row 6+7: tasks + evidence */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ChipPicker
          label="Required Tasks"
          value={visit.required_tasks ?? []}
          suggestions={TASK_SUGGESTIONS}
          onChange={(v) => onChange({ required_tasks: v })}
        />
        <ChipPicker
          label="Required Evidence"
          value={visit.required_evidence ?? []}
          suggestions={EVIDENCE_SUGGESTIONS}
          onChange={(v) => onChange({ required_evidence: v })}
        />
      </div>

      {/* Row 8: effective dates */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Effective From</label>
          <input
            type="date"
            value={visit.effective_from ?? ''}
            onChange={(e) => onChange({ effective_from: e.target.value })}
            className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Effective To <span className="text-slate-400">(optional)</span>
          </label>
          <input
            type="date"
            value={visit.effective_to ?? ''}
            onChange={(e) => onChange({ effective_to: e.target.value || null })}
            className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm"
          />
        </div>
      </div>
    </div>
  );
}

// ── Main editor ────────────────────────────────────────────────────────

interface PatrolPlanEditorProps {
  /** Existing plan to edit.  If null, `coverageId` must be provided so we
   *  can create a new plan on save. */
  plan: PatrolServicePlan | null;
  /** Required when `plan` is null; ignored when editing an existing plan. */
  coverageId?: string;
  onSaved?: (plan: PatrolServicePlan) => void;
  onCancel?: () => void;
}

export default function PatrolPlanEditor({ plan, coverageId, onSaved, onCancel }: PatrolPlanEditorProps) {
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState<string>(plan?.notes ?? '');
  const [visits, setVisits] = useState<EditableVisit[]>(() =>
    (plan?.required_visits ?? []).map((v) => ({ ...v, _key: v.id ?? nextKey() })),
  );
  const [error, setError] = useState<string | null>(null);

  // Keep local state fresh if the parent hands us a different plan
  useEffect(() => {
    setNotes(plan?.notes ?? '');
    setVisits((plan?.required_visits ?? []).map((v) => ({ ...v, _key: v.id ?? nextKey() })));
  }, [plan?.id]);

  const patchVisit = (key: string, patch: Partial<EditableVisit>) =>
    setVisits((prev) => prev.map((v) => (v._key === key ? { ...v, ...patch } : v)));

  const addVisit = () =>
    setVisits((prev) => [...prev, emptyVisit(prev.length)]);

  const removeVisit = (key: string) =>
    setVisits((prev) => prev.filter((v) => v._key !== key));

  const move = (idx: number, delta: number) =>
    setVisits((prev) => {
      const next = prev.slice();
      const target = idx + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next.map((v, i) => ({ ...v, display_order: i }));
    });

  const buildPayloadVisits = () =>
    visits.map((v, i) => {
      // Preserve id ONLY when it's a real server id (not a client _key).
      const payload: Partial<RequiredVisit> & { id?: string } = {
        visit_type: v.visit_type as VisitType,
        time_window_type: v.time_window_type as TimeWindowType,
        time_window_start: v.time_window_start ?? null,
        time_window_end: v.time_window_type === 'fixed' ? null : (v.time_window_end ?? null),
        days_of_week: v.days_of_week ?? [],
        frequency_per_day: v.frequency_per_day ?? 1,
        expected_duration_min_minutes: v.expected_duration_min_minutes ?? null,
        expected_duration_max_minutes: v.expected_duration_max_minutes ?? null,
        required_tasks: v.required_tasks ?? [],
        required_evidence: v.required_evidence ?? [],
        criticality: (v.criticality as VisitCriticality) ?? 'required',
        effective_from: v.effective_from ?? new Date().toISOString().slice(0, 10),
        effective_to: v.effective_to ?? null,
        display_order: i,
        label: v.label ?? '',
      };
      if (v.id) payload.id = v.id;
      return payload;
    });

  // Client-side pre-flight — the backend enforces all of these, but
  // catching them here gives per-row feedback instead of a single 400
  // that rejects the whole plan.
  const validate = (): string | null => {
    for (let i = 0; i < visits.length; i++) {
      const v = visits[i];
      const label = v.label || `Visit ${i + 1}`;
      if (!v.days_of_week || v.days_of_week.length === 0) {
        return `${label}: pick at least one day of the week.`;
      }
      if (v.time_window_type !== 'fixed') {
        if (!v.time_window_start || !v.time_window_end) {
          return `${label}: both start and end times are required for window/broad/random.`;
        }
        if (v.time_window_start >= v.time_window_end) {
          // Wraparound-midnight windows (start > end) get treated as
          // valid by the matcher; only reject the equal case here.
          if (v.time_window_start === v.time_window_end) {
            return `${label}: start and end times can't be identical.`;
          }
        }
      } else if (!v.time_window_start) {
        return `${label}: a start time is required for a fixed visit.`;
      }
      if ((v.frequency_per_day ?? 1) < 1) {
        return `${label}: frequency per day must be at least 1.`;
      }
      if (
        v.expected_duration_min_minutes != null
        && v.expected_duration_max_minutes != null
        && v.expected_duration_min_minutes > v.expected_duration_max_minutes
      ) {
        return `${label}: duration min must be ≤ duration max.`;
      }
    }
    return null;
  };

  const mutation = useMutation({
    mutationFn: async () => {
      const localError = validate();
      if (localError) throw new Error(localError);
      const payload = { notes, required_visits: buildPayloadVisits() };
      if (plan) {
        const r = await clientApi.updatePatrolPlan(plan.id, payload);
        return r.data;
      } else {
        if (!coverageId) throw new Error('coverageId is required when creating a new plan.');
        const r = await clientApi.createPatrolPlan({ coverage_id: coverageId, ...payload });
        return r.data;
      }
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['client', 'patrol-plans'] });
      onSaved?.(saved);
    },
    onError: (err: unknown) => setError(extractApiError(err)),
  });

  const disable = mutation.isPending || (!plan && !coverageId);

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>
      )}

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Plan Notes <span className="text-slate-400">(optional)</span>
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          placeholder="Anything the vendor should know about the overall patrol expectation."
        />
      </div>

      <div className="space-y-3">
        {visits.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500 text-center">
            No required visits yet. Add one below to define what you expect the vendor to do.
          </div>
        )}
        {visits.map((v, i) => (
          <VisitRow
            key={v._key}
            visit={v}
            onChange={(patch) => patchVisit(v._key, patch)}
            onDelete={() => removeVisit(v._key)}
            onMoveUp={() => move(i, -1)}
            onMoveDown={() => move(i, +1)}
            canMoveUp={i > 0}
            canMoveDown={i < visits.length - 1}
          />
        ))}
      </div>

      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={addVisit}
          className="px-3 py-1.5 text-sm rounded-md border border-brand-300 text-brand-700 hover:bg-brand-50"
        >
          + Add required visit
        </button>
        <div className="flex gap-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={disable}
            className="px-4 py-1.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-md disabled:opacity-50"
          >
            {mutation.isPending ? 'Saving...' : plan ? 'Save Changes' : 'Create Plan'}
          </button>
        </div>
      </div>
    </div>
  );
}
